import {Circle, Container, Point, Rectangle} from 'pixi.js';

import {BaseComponent, IVector2, Transform2dComponent} from '@specials/gcs-engine';

export enum ViewComponentShapeType {
  Rectangle = 'Rectangle',
  // у окружности, нарисованной с помощью Graphics.drawRect
  // центр координат находится в центр окружности
  GraphicsCircle = 'GraphicsCircle',
  // а у спрайтовой окружности центр координат в левом верхнем углу
  SpriteCircle = 'SpriteCircle',
}

export abstract class PixiViewComponent<V extends Container = Container> extends BaseComponent {
  protected transformComponent: Transform2dComponent;
  public shapeType: ViewComponentShapeType = ViewComponentShapeType.Rectangle;

  public view: V;

  constructor() {
    super();
    this.tag = 'ViewComponent';
  }

  public onAddToWorld(): void {
    this.transformComponent = this.gameObject.getComponent<Transform2dComponent>(Transform2dComponent)!;
    this.view = this.renderView();
  }

  protected abstract renderView(): V;

  get center(): IVector2 {
    return new Point(
      this.transformComponent.x + (0.5 - this.anchor.x) * this.width,
      this.transformComponent.y + (0.5 - this.anchor.y) * this.height,
    );
  }

  // прямоугольник коллизий объекта
  get intersectRectBounds(): Rectangle {
    return new Rectangle(
      this.transformComponent.position.x - this.anchor.x * this.width,
      this.transformComponent.position.y - this.anchor.y * this.height,
      this.width,
      this.height,
    );
  }

  // окружность коллизий объекта
  get intersectCircleBounds(): Circle {
    // (в коллизиях используется центр окружности, поэтому передаем его)
    if (this.isSpriteCircle()) {
      return new Circle(
        this.transformComponent.x + (0.5 - this.anchor.x) * this.width,
        this.transformComponent.y + (0.5 - this.anchor.y) * this.height,
        this.width / 2,
      );
    }

    return new Circle(this.transformComponent.position.x, this.transformComponent.position.y, this.width / 2);
  }

  get bounds(): Rectangle {
    const x = this.transformComponent.position.x - this.anchor.x * this.width;
    const y = this.transformComponent.position.y - this.anchor.y * this.height;

    return new Rectangle(x, y, x + this.width, y + this.height);
  }

  set width(w: number) {
    this.view.width = w;
  }

  get width(): number {
    return this.view.width;
  }

  get halfWidth(): number {
    return this.view.width / 2;
  }

  get height(): number {
    return this.view.height;
  }

  get halfHeight(): number {
    return this.view.height / 2;
  }

  set height(h: number) {
    this.view.height = h;
  }

  set scale(v: number) {
    this.view.scale.set(v);
  }

  get anchor(): Point {
    return 'anchor' in this.view ? (this.view.anchor as Point) : new Point();
  }

  get ratio(): number {
    return this.width / this.height;
  }

  public isSpriteCircle(): boolean {
    return this.shapeType === ViewComponentShapeType.SpriteCircle;
  }

  public isRectangle(): boolean {
    return this.shapeType === ViewComponentShapeType.Rectangle;
  }

  public isGraphicsCircle(): boolean {
    return this.shapeType === ViewComponentShapeType.GraphicsCircle;
  }
}
